/* eslint-disable no-return-assign */
import {getWebshopData} from '@/util/getWebshopData.js';
import clonedeep from 'lodash.clonedeep';

// helper functions
const checkForCustomProduct = (products) => products.filter((product) => product?.customContent);

const getMissingProduct = (rootState, dispatch, prodKey, stepName = false) => {
	const missingProduct = getWebshopData(rootState.productsList[prodKey], rootState.COUNTRY);
	dispatch('setMissingProducts', {missingProduct, stepName});

	const foundMissing = [missingProduct].filter((prod) => {
		if (!prod) return false;
		// remove any products that:
		// are not published
		if (!prod.publishingStatus === 'PUBLISHED') return false;

		// considered phased out and phasing out date is before today
		if (prod.phasingOut) {
			// if phasedOut and doesn't have phasingOutDate
			if (!prod.phasingOutDate) return false;
			const phDate = new Date(prod.phasingOutDate * 1000);
			const today = new Date();
			// if phasingOut is true and the date is before
			if (phDate.getTime() < today.getTime()) return false;
		}
		return true;
	});

	// return missing product w/ updated rules object OR null
	return foundMissing[0]
		? {
				...foundMissing[0],
				...{
					rules: {
						excludeBy: new Set(),
						exclusiveBy: new Set(),
						exclusiveRequiredBy: new Set(),
						requiredBy: new Set(),
						suggestedBy: new Set(),
						suggestedRan: new Set(['ran']),
					},
				},
		  }
		: null;
};

const state = {
	countryAvailabilityChanged: null,
	isRestoredWorkflow: null,
	missingProducts: {
		missing: new Set(),
		found: {},
	},
};

const mutations = {
	setCountryAvailabilityChanged: (state, bool) => (state.countryAvailabilityChanged = bool),
	isRestoredWorkflow: (state, bool) => (state.isRestoredWorkflow = bool),
	setMissingProducts: (state, obj) => (state.missingProducts = obj),
};

const actions = {
	setCountryAvailabilityChanged: ({commit}, bool) => commit('setCountryAvailabilityChanged', bool),
	isRestoredWorkflow: ({commit}, bool) => commit('isRestoredWorkflow', bool),
	setMissingProducts: ({commit, getters, rootState}, {missingProduct = false, stepName}) => {
		// get readable name if found or default to name given
		const name = rootState.cvlWebSteps.values.find((el) => el.key === stepName)?.value?.enUS ?? stepName;
		const MP = getters.getMissingProducts;

		// product is still in Products.json
		if (missingProduct) {
			// build step group if needed
			if (!MP.found[stepName]) MP.found[stepName] = {name, products: []};

			// // add product info to object
			const {catalogNumber, description} = missingProduct;
			MP.found[stepName].products.push({catalogNumber, description});
		}

		// product not found in Products.json
		else MP.missing.add(name);

		// commit to state
		return commit('setMissingProducts', MP);
	},
	restoreWfs: ({commit, dispatch, rootState, rootGetters}, wfs) => {
		const wfSteps = rootGetters['workflow/wfSteps'];
		const stepKeyIndex = rootState.hcube.stepsList[0];
		const allSteps = [];

		const getStepIndex = (name, i) => {
			// try to find step index in hcube
			const index = stepKeyIndex.findIndex((key) => key === name);
			// * returns index if found || if i is zero -1 to stay first in list
			// * else return the previous steps index + .1  to keep in the correct place

			return index >= 0 || i === 0 ? index : wfs[--i].index + 0.1;
		};

		// reset missing product content
		commit('setMissingProducts', {missing: new Set(), found: {}});

		// set flag
		commit('isRestoredWorkflow', true);

		for (const [i, myWFStep] of wfs.entries()) {
			// handle custom steps
			if (myWFStep?.customContent) {
				allSteps.push(myWFStep);
				continue;
			}
			const {stepEnabled, name} = myWFStep;

			// pre labNotes fallback
			const labNotes = myWFStep.labNotes || {desc: ''};

			// get step by name or build step if saved step is no longer available in workflow
			const stepToUpdate = wfSteps.find((step) => step.name === myWFStep.name) ?? {
				filterCategories: {},
				// get index by key from hcube steplist
				index: getStepIndex(myWFStep.name, i),
				labNotes: {desc: ''},
				name: myWFStep.name,
				products: [],
				selected: [{initial: '', updated: ''}],
				stepEnabled: null,
			};

			// restore any products from saved workflow that are no longer in the default workflow
			for (const key of myWFStep.products) {
				// don't care about custom steps or products already in the default step
				if (isNaN(key) || stepToUpdate.products.some((product) => product.pimId === key)) continue;
				const missingProduct = getMissingProduct(rootState, dispatch, key, myWFStep.name);
				if (missingProduct) stepToUpdate.products.push(missingProduct);
			}

			// set flag for suggestedProducts so we don't overwirte saved workflow
			// stops rules from running when a saved workflow is loaded
			for (const product of stepToUpdate.products)
				if (product?.rules) product.rules.suggestedRan = new Set(['ran']);
				else
					product.rules = {
						excludeBy: new Set(),
						exclusiveBy: new Set(),
						exclusiveRequiredBy: new Set(),
						requiredBy: new Set(),
						suggestedBy: new Set(),
						suggestedRan: new Set(['ran']),
					};

			// adds custom products
			const hasCustomProduct = checkForCustomProduct(myWFStep.products);
			if (hasCustomProduct?.length) {
				for (const custProd of hasCustomProduct) {
					const {key, customContent} = custProd;
					stepToUpdate.products.push({name, key, customContent});
				}
			}
			// add filters for products
			// TBD: find out if this code is still needed
			myWFStep?.filters?.forEach((filter) => {
				const oldFilters = stepToUpdate.filterCategories[filter.name]?.filterGroup.selectedFilters;
				if (!oldFilters) return;

				oldFilters.temp = filter.selectedFilters;
				oldFilters.updated = filter.selectedFilters;
			});

			// set selected products
			const mySelected = [];

			// add selected product to mySelected array if product is in product list
			for (const selected of myWFStep.selectedProduct)
				if (stepToUpdate.products.some((prod) => prod.key === selected.initial)) mySelected.push(selected);

			// overwrite selected products to selected from restored workflow
			stepToUpdate.selected = mySelected;

			// add updated step to allSteps
			allSteps.push({
				...stepToUpdate,
				stepEnabled,
				name,
				labNotes,
			});
		}

		// update workflow in store to the restored version
		commit('workflow/setWfSteps', allSteps, {root: true});
	},
};

const getters = {
	countryAvailabilityChanged: (state) => state.countryAvailabilityChanged,
	isRestoredWorkflow: (state) => state.isRestoredWorkflow,
	getMissingProducts: (state) => clonedeep(state.missingProducts),
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
};
